import { DiscountGroup, ProductGroupCount } from '@retail/products/types';
import {
  AllFilters,
  FILTER_CONFIG,
  FilterKey,
  isMemoryFilterKey,
  MemoryFilterKey,
} from '@retail/products/utils';
import { SupplierBase } from '@retail/suppliers/types';
import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { create } from 'zustand/react';
import { trackFilterChanges, trackFiltersReset } from './filterTracking';

export interface FilterData {
  suppliers: SupplierBase[];
  discountGroups: DiscountGroup[];
  productGroups: ProductGroupCount[];
}

type MemoryFilters = Partial<Pick<AllFilters, MemoryFilterKey>>;

interface MemoryFiltersState {
  filtersByPathname: Record<string, MemoryFilters>;
  setMemoryFilters: (pathname: string, updates: MemoryFilters) => void;
  resetMemoryFilters: (pathname: string, keys: MemoryFilterKey[]) => void;
}

export const useMemoryFiltersStore = create<MemoryFiltersState>((set) => ({
  filtersByPathname: {},
  setMemoryFilters: (pathname, updates) =>
    set(({ filtersByPathname }) => ({
      filtersByPathname: {
        ...filtersByPathname,
        [pathname]: { ...filtersByPathname[pathname], ...updates },
      },
    })),
  resetMemoryFilters: (pathname, keys) =>
    set(({ filtersByPathname }) => {
      if (!filtersByPathname[pathname] || !keys.length) return { filtersByPathname };
      const next = { ...filtersByPathname[pathname] };
      keys.forEach((key) => delete next[key]);
      return { filtersByPathname: { ...filtersByPathname, [pathname]: next } };
    }),
}));

export const useFilters = <K extends FilterKey>(selectedFilters: readonly K[]) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const memoryFilters = useMemoryFiltersStore((state) => state.filtersByPathname[pathname]);
  const setMemoryFilters = useMemoryFiltersStore((state) => state.setMemoryFilters);
  const resetMemoryFilters = useMemoryFiltersStore((state) => state.resetMemoryFilters);

  const serializedKeys = selectedFilters.join(',');

  const filters = useMemo(() => {
    const result = {} as Pick<AllFilters, K>;

    selectedFilters.forEach((key) => {
      if (isMemoryFilterKey(key)) {
        result[key] = (memoryFilters?.[key] ?? null) as any;
        return;
      }

      const conf = FILTER_CONFIG[key];
      const rawValue = searchParams.get(conf.urlKey);

      if (conf.type === 'list' || conf.type === 'range') {
        result[key] = (rawValue ? rawValue.split(',').filter(Boolean) : []) as any;
      } else if (conf.type === 'boolean') {
        result[key] = (searchParams.has(conf.urlKey) && rawValue !== 'false') as any;
      } else if (conf.type === 'group') {
        result[key] = (rawValue ?? 'UNSPECIFIED') as AllFilters[K];
      } else {
        // Håndterer både string og group
        result[key] = (rawValue ?? ('default' in conf ? (conf as any).default : null)) as any;
      }
    });

    return result;
  }, [serializedKeys, searchParams, memoryFilters]);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const setFilters = useCallback(
    (updates: { [P in K]?: AllFilters[P] | undefined }) => {
      trackFilterChanges(pathname, filtersRef.current, updates);
      const entries = Object.entries(updates) as [FilterKey, unknown][];
      const memoryEntries = entries.filter(([key]) => isMemoryFilterKey(key));
      const urlEntries = entries.filter(([key]) => !isMemoryFilterKey(key));

      if (memoryEntries.length) {
        setMemoryFilters(
          pathname,
          Object.fromEntries(memoryEntries.map(([key, value]) => [key, value ?? null]))
        );
      }

      setSearchParams(
        (prevParams) => {
          const nextParams = new URLSearchParams(prevParams);

          urlEntries.forEach(([key, value]) => {
            const urlKey = FILTER_CONFIG[key].urlKey;

            if (
              value === undefined ||
              value === null ||
              value === '' ||
              value === false ||
              value === 'UNSPECIFIED' ||
              (Array.isArray(value) && value.length === 0)
            ) {
              nextParams.delete(urlKey);
            } else if (Array.isArray(value)) {
              nextParams.set(urlKey, value.join(','));
            } else {
              nextParams.set(urlKey, String(value));
            }
          });

          return nextParams;
        },
        { replace: true }
      );
    },
    [pathname, setMemoryFilters, setSearchParams]
  );

  const updateFilter = useCallback(
    <Key extends K>(key: Key, value: AllFilters[Key] extends (infer U)[] ? U : AllFilters[Key]) => {
      const config = FILTER_CONFIG[key];
      const currentValue = filters[key];

      if (config.type === 'list' && Array.isArray(currentValue)) {
        const stringVal = String(value);
        const nextList = currentValue.includes(stringVal)
          ? currentValue.filter((item) => item !== stringVal) // Fjern hvis finnes
          : [...currentValue, stringVal]; // Legg til hvis ny

        setFilters({ [key]: nextList } as any);
        return;
      }

      if (config.type === 'boolean') {
        setFilters({ [key]: !currentValue } as any);
        return;
      }

      if (value === 'UNSPECIFIED') {
        setFilters({ [key]: undefined } as any);
      } else {
        setFilters({ [key]: value } as any);
      }
    },
    [serializedKeys, setFilters]
  );

  const resetFilters = useCallback(
    (additionalFilters: string[] = [], paramsToMutate?: URLSearchParams) => {
      const nextParams = paramsToMutate ?? new URLSearchParams(searchParams);

      trackFiltersReset(pathname, filtersRef.current);
      resetMemoryFilters(pathname, selectedFilters.filter(isMemoryFilterKey) as MemoryFilterKey[]);

      selectedFilters.forEach((key) => {
        if (FILTER_CONFIG[key]) {
          nextParams.delete(FILTER_CONFIG[key].urlKey);
        }
      });

      additionalFilters.forEach((key) => {
        nextParams.delete(key);
      });

      if (!paramsToMutate) {
        setSearchParams(nextParams, { replace: true });
      }
    },
    [serializedKeys, pathname, resetMemoryFilters, setSearchParams]
  );

  return {
    filters,
    setFilters,
    updateFilter,
    resetFilters,
  };
};
